import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import VSLogo from "@/components/VSLogo";
import {
  LayoutDashboard, Search, Bell, Briefcase, Building2, FileText, Users, UserCog, Calendar, FileSignature,
  ClipboardList, BrainCircuit, Wand2, LineChart, Newspaper, CalendarDays, Trophy, Users2,
  LifeBuoy, BellRing, Mail, MailOpen, Bot, Activity, Shield, ScrollText, LockKeyhole, Heart,
  Settings, Globe, LogOut, ChevronLeft, ChevronRight, Menu, X, Sparkles, ShieldCheck, BookOpen,
} from "lucide-react";
import { loadUser, clearAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import GlobalSearch from "@/components/GlobalSearch";

const GROUPS = [
  {
    label: "OVERVIEW",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "hr_admin"] },
      { to: "/recruiter/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["recruiter"] },
      { to: "/admin/search", label: "Global Search", icon: Search, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/notifications", label: "Notifications", icon: Bell, roles: ["super_admin", "hr_admin", "recruiter"] },
    ],
  },
  {
    label: "RECRUITMENT",
    items: [
      { to: "/admin/jobs", label: "Jobs", icon: Briefcase, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/career-domains", label: "Career Domains", icon: Building2, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/applications", label: "Applications", icon: FileText, roles: ["super_admin", "hr_admin"] },
      { to: "/recruiter/applications", label: "Applications", icon: FileText, roles: ["recruiter"] },
      { to: "/admin/candidates", label: "Candidates", icon: Users, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/recruiters", label: "Recruiters", icon: UserCog, roles: ["super_admin"] },
      { to: "/admin/interviews", label: "Interviews", icon: Calendar, roles: ["super_admin", "hr_admin", "recruiter"] },
      { to: "/admin/offers", label: "Offers", icon: FileSignature, roles: ["super_admin", "hr_admin"] },
    ],
  },
  {
    label: "ASSESSMENT",
    items: [
      { to: "/admin/assessments", label: "Assessments", icon: ClipboardList, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/ai-questions", label: "AI Questions", icon: Wand2, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/jd-generator", label: "JD Generator", icon: Sparkles, roles: ["super_admin", "hr_admin"] },
    ],
  },
  {
    label: "CAREER HUB",
    items: [
      { to: "/admin/companies", label: "Companies", icon: Building2, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/external-opportunities", label: "External Opportunities", icon: Globe, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/experience-projects", label: "Experience Projects", icon: BrainCircuit, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/practice-challenges", label: "Practice Challenges", icon: ShieldCheck, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/career-resources", label: "Career Resources", icon: BookOpen, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/career-roadmaps", label: "Career Roadmaps", icon: LineChart, roles: ["super_admin"] },
    ],
  },
  {
    label: "CAREERS",
    items: [
      { to: "/admin/career-updates", label: "Career Updates", icon: Newspaper, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/career-events", label: "Career Events", icon: CalendarDays, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/hackathons", label: "Hackathons", icon: Trophy, roles: ["super_admin", "hr_admin"] },
    ],
  },
  {
    label: "CANDIDATE SUPPORT",
    items: [
      { to: "/admin/support", label: "Support Tickets", icon: LifeBuoy, roles: ["super_admin", "hr_admin"] },
    ],
  },
  {
    label: "COMMUNICATION",
    items: [
      { to: "/admin/email-templates", label: "Email Templates", icon: Mail, roles: ["super_admin", "hr_admin"] },
      { to: "/admin/email-logs", label: "Email Logs", icon: MailOpen, roles: ["super_admin", "hr_admin"] },
    ],
  },
  {
    label: "AI",
    items: [
      { to: "/admin/settings/vs-ai", label: "VS AI Settings", icon: Bot, roles: ["super_admin"] },
    ],
  },
  {
    label: "MANAGEMENT",
    items: [
      { to: "/admin/users", label: "Users & Roles", icon: Users2, roles: ["super_admin"] },
      { to: "/admin/audit-logs", label: "Audit Logs", icon: ScrollText, roles: ["super_admin"] },
      { to: "/admin/login-security", label: "Login Security", icon: LockKeyhole, roles: ["super_admin"] },
      { to: "/admin/system-health", label: "System Health", icon: Activity, roles: ["super_admin", "hr_admin"] },
    ],
  },
];

export default function AdminLayout({ children }) {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const user = loadUser();
  const role = user?.role || "";
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const logout = () => { clearAuth(); nav("/admin/login"); };

  const visibleGroups = GROUPS.map(g => ({
    ...g, items: g.items.filter(i => i.roles.includes(role)),
  })).filter(g => g.items.length > 0);

  const NavContent = (
    <>
      <div className="p-5 border-b border-slate-800 flex items-center gap-2">
        <VSLogo dark size={28} />
        {!collapsed && (
          <div>
            <div className="text-sm font-semibold text-white">VS Management</div>
            <div className="text-[10px] uppercase tracking-widest text-[#F59E0B]">Careers Portal</div>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-4">
        {visibleGroups.map(group => (
          <div key={group.label}>
            {!collapsed && (
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                {group.label}
              </div>
            )}
            <div className="space-y-0.5 mt-1">
              {group.items.map(({ to, label, icon: Icon }) => {
                const active = pathname === to || pathname.startsWith(to + "/");
                return (
                  <Link key={to} to={to}
                    data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setMobileOpen(false)}
                    title={collapsed ? label : undefined}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                      active
                        ? "bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30"
                        : "text-slate-400 hover:text-white hover:bg-slate-800"
                    }`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    {!collapsed && <span className="truncate">{label}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-4 border-t border-slate-800">
        {!collapsed && (
          <>
            <div className="text-xs text-slate-400">Logged in as</div>
            <div className="text-sm text-white font-medium truncate">{user?.full_name}</div>
            <div className="text-[11px] text-[#F59E0B] uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
          </>
        )}
        <Button onClick={logout} variant="ghost" size="sm"
                className="mt-3 w-full text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="admin-logout">
          <LogOut className="w-4 h-4 mr-1.5" />
          {!collapsed && "Logout"}
        </Button>
        <button onClick={() => setCollapsed(c => !c)}
                data-testid="admin-sidebar-toggle"
                className="mt-2 w-full flex justify-center text-slate-500 hover:text-white p-1 rounded">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Desktop sidebar */}
      <aside className={`fixed inset-y-0 left-0 ${collapsed ? "w-16" : "w-60"} bg-slate-900 border-r border-slate-800 hidden md:flex flex-col transition-[width] duration-200`}>
        {NavContent}
      </aside>

      {/* Mobile slide-out */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setMobileOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-slate-900 border-r border-slate-800 z-50 flex flex-col md:hidden">
            <div className="absolute top-3 right-3">
              <Button size="icon" variant="ghost" onClick={() => setMobileOpen(false)} className="text-slate-400">
                <X className="w-5 h-5" />
              </Button>
            </div>
            {NavContent}
          </aside>
        </>
      )}

      <main className={`${collapsed ? "md:pl-16" : "md:pl-60"} min-h-screen transition-[padding] duration-200`}>
        <div className="md:hidden bg-slate-900 border-b border-slate-800 p-3 flex justify-between items-center">
          <Button size="icon" variant="ghost" onClick={() => setMobileOpen(true)} data-testid="admin-mobile-menu">
            <Menu className="w-5 h-5 text-slate-300" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#F59E0B]" />
            <span className="font-semibold text-white text-sm">VS Management</span>
          </div>
          <Button onClick={logout} size="icon" variant="ghost" className="text-slate-400">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
        {user && user.role !== "candidate" && (
          <div className="hidden md:flex bg-slate-900 border-b border-slate-800 px-6 py-3">
            <GlobalSearch />
          </div>
        )}
        <div className="p-6 md:p-10">{children}</div>
      </main>
    </div>
  );
}
